import { useEffect, useMemo, useState } from "react"
import { FormGroup, Label, Button, Row, Col, Spinner } from "reactstrap"
import CustomerSearch from "./CustomerSearch"
import QuotationCustomerForm from "./QuotationCustomerForm"
import { useCustomerInfo } from "hooks/useCustomer"
import { ProfileCard } from "pages/CustomerPages/CustomerProfile/Profile"
import ThemeModal from "components/Common/ThemeModal"
import DynamicField from "components/Common/DynamicField"

const CustomerSelector = ({
  apiSearchFunction,
  formik,
  isSearching,
  selectedCustomer,
  setSelectedCustomer,
  existCustomerData,
  isEdit = false,
  lockCustomer = false,
}) => {
  const [showCustomerProfile, setShowCustomerProfile] = useState(false)
  const [showCustomerForm, setShowCustomerForm] = useState(false)

  const onChange = formik.handleChange
  const { setFieldValue } = formik
  const error = formik.errors.customer_info
  const touched = formik.touched.customer_info

  const { getQuotationCustomerRichText, getCustomerInfo } = useCustomerInfo()

  const handleCustomerSelect = customer => {
    setSelectedCustomer(customer)

    const customerRichText =
      getQuotationCustomerRichText(customer) || customer?.name || "-"

    setFieldValue("customer_info", customerRichText)
    setFieldValue("customer_signatory_name", customer?.name ?? "")
  }

  useEffect(() => {
    if (existCustomerData) {
      handleCustomerSelect(existCustomerData)
    }
  }, [existCustomerData])

  const clearSelection = () => {
    setSelectedCustomer(null)
    setFieldValue("customer_info", "")
    setFieldValue("customer_signatory_name", "")
  }

  const currentCustomerInfo = useMemo(
    () => getCustomerInfo(selectedCustomer),
    [selectedCustomer]
  )

  const initialData = selectedCustomer || {}

  const onCustomer = (customerInfo, initialData) => {
    handleCustomerSelect({ ...initialData, ...customerInfo })
  }

  return (
    <FormGroup>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Label className="fw-bold mb-0">
          To:
          <span className="text-danger"> *</span>
        </Label>

        {selectedCustomer && (
          <div className="d-flex flex-wrap align-items-center gap-1">
            {!isEdit && !lockCustomer && (
              <Button
                color="outline-danger me-1"
                size="sm"
                onClick={clearSelection}
              >
                <i className="bx bx-x"></i> Clear
              </Button>
            )}
            <Button
              color="outline-success me-1"
              size="sm"
              onClick={() => handleCustomerSelect(selectedCustomer)}
            >
              <i className="fas fa-user-tag"></i> Refill
            </Button>
            <Button
              color="outline-primary me-1"
              size="sm"
              onClick={() => setShowCustomerProfile(true)}
            >
              <i className="fas fa-eye"></i> Profile
            </Button>
          </div>
        )}
      </div>

      

      {!selectedCustomer ? (
        <div className="customer-selector position-relative">
          <Row className="g-2">
            <Col xs={12} md={8}>
              <CustomerSearch
                onCustomerSelect={handleCustomerSelect}
                apiSearchFunction={apiSearchFunction}
                placeholder="Search existing customers..."
                isSearching={isSearching}
              />
            </Col>
            <Col xs={12} md={4}>
              <Button
                color="primary"
                className="w-100"
                onClick={() => setShowCustomerForm(true)}
                disabled={isSearching}
              >
                <i className="bx bx-user-plus me-1"></i>
                New Customer
              </Button>
            </Col>
          </Row>
          {isSearching && (
            <div
              className="position-absolute w-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
              style={{ top: 0, left: 0, right: 0, bottom: 0, zIndex: 4, pointerEvents: "none" }}
            >
              <Spinner size="sm" className="me-2" />
              <small className="text-muted">Loading customer...</small>
            </div>
          )}
        </div>
      ) : (
        <>
          <FormGroup>
            <div className="position-relative">
              <DynamicField
                type="richtext"
                name="customer_info"
                value={formik.values.customer_info}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                setFieldValue={formik.setFieldValue}
                placeholder="Enter customer details..."
                required
                height={100}
                invalid={
                  formik.touched.customer_info &&
                  Boolean(formik.errors.customer_info)
                }
                feedback={
                  formik.touched.customer_info && formik.errors.customer_info
                }
              />
            </div>
          </FormGroup>
        </>
      )}

      {touched && error && (
        <div className="invalid-feedback d-block">{error}</div>
      )}

      <ThemeModal
        isOpen={showCustomerProfile}
        toggle={() => setShowCustomerProfile(false)}
        title="Customer Profile"
        size="lg"
      >
        <ProfileCard
          data={currentCustomerInfo}
          loading={false}
          onCustomer={onCustomer}
        />
      </ThemeModal>

      <QuotationCustomerForm
        isOpen={showCustomerForm}
        toggle={() => setShowCustomerForm(prev => !prev)}
        onCustomer={onCustomer}
        initialData={initialData}
        title={`${initialData?.id ? "Edit New Customer" : "Add New Customer"}`}
        submitText={`${initialData?.id ? "Edit Customer" : "Add Customer"}`}
      />
    </FormGroup>
  )
}

export default CustomerSelector
